// toast-store.ts — module-level toast queue.
//
// Plan ref: 06-06-PLAN-cmd-k-toast-empty-loading-error.md §Task 2 step 1.
//
// Why module-level: useToast.push must be callable from anywhere (mutation
// success handlers, error boundaries, sync click handlers in lanes), so
// the source of truth lives outside React state. The Provider subscribes
// via useSyncExternalStore.

import type { ToastEntry } from './Toast';
import type { ToastInput } from './useToast';

const MAX_TOASTS = 3;

let entries: readonly ToastEntry[] = [];
const listeners = new Set<() => void>();
let counter = 0;

function emit() {
  for (const l of listeners) l();
}

function nextId(): string {
  counter += 1;
  // Math.random keeps IDs unique even after restart — combined with a
  // monotonic counter it avoids collisions when several pushes happen in
  // the same millisecond.
  return `toast-${counter}-${Math.random().toString(36).slice(2, 8)}`;
}

export function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function getEntries(): readonly ToastEntry[] {
  return entries;
}

export function push(input: ToastInput): string {
  const id = nextId();
  const entry: ToastEntry =
    input.description !== undefined
      ? { id, title: input.title, description: input.description, variant: input.variant }
      : { id, title: input.title, variant: input.variant };
  // Append, then evict oldest if we exceed the cap. FIFO eviction so a
  // burst of mutations leaves the most-recent visible.
  const next = [...entries, entry];
  entries = next.length > MAX_TOASTS ? next.slice(next.length - MAX_TOASTS) : next;
  emit();
  return id;
}

export function dismiss(id: string): void {
  if (!entries.some((e) => e.id === id)) return;
  entries = entries.filter((e) => e.id !== id);
  emit();
}

/** Test helper — clear the queue between tests. */
export function __resetForTests(): void {
  entries = [];
  emit();
}
